import { useInfiniteScroll } from '@ibsys/kit';
import { FetchNextPageOptions, InfiniteData, InfiniteQueryObserverResult } from '@tanstack/react-query';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

/**
 * Параметры бесконечной прокрутки с защитой от бесплодных повторов подгрузки.
 */
export type UseGuardedInfiniteScrollParams<TPage, TError = unknown> = {
  /** `fetchNextPage` из `useInfiniteQuery` */
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<InfiniteQueryObserverResult<InfiniteData<TPage>, TError>>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** Количество уже загруженных порций — `data?.pages.length` */
  pagesCount: number;
  /**
   * Смена значения возобновляет остановленную автоподгрузку: список начинается заново (поиск, фильтр).
   *
   * Только примитив — сравнение идёт по значению. Составной ключ из нескольких условий потребитель
   * сводит к строке сам (`${search}|${groupId}`): объект, собранный в теле рендера, был бы новым на
   * каждом рендере и снимал бы остановку сразу после её установки.
   */
  resetKey?: string | number | boolean | null;
  /** Отступ упреждающего срабатывания маркера, px */
  threshold?: number;
  /** Прокручиваемый контейнер списка */
  root?: RefObject<HTMLElement | null>;
};

/**
 * Бесконечная прокрутка, в которой попытка подгрузки не повторяется сама по себе.
 *
 * Наблюдатель маркера из `@ibsys/kit` пересоздаётся после каждой завершившейся попытки и при
 * создании сразу сообщает о пересечении. Поэтому попытка, не пополнившая список, повторялась бы
 * без пауз и без ограничения числа повторов, пока маркер остаётся в зоне упреждения.
 *
 * Здесь одновременно идёт не больше одной попытки (иначе повторное обращение отменило бы уже
 * идущее), а попытка, не пополнившая список при заявленном продолжении, останавливает
 * автоподгрузку до `resumeAutoLoad` или до смены `resetKey`.
 *
 * `isAutoLoadStopped` означает именно отказ, а не конец списка, поэтому потребитель показывает
 * по нему сообщение и повтор, не сверяясь дополнительно с `hasNextPage`.
 */
export const useGuardedInfiniteScroll = <TPage, TError = unknown>({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  pagesCount,
  resetKey,
  threshold,
  root,
}: UseGuardedInfiniteScrollParams<TPage, TError>) => {
  const [isAutoLoadStopped, setIsAutoLoadStopped] = useState(false);
  const isLoadingRef = useRef(false);
  const pagesCountRef = useRef(pagesCount);
  const resetKeyRef = useRef(resetKey);

  useEffect(() => {
    pagesCountRef.current = pagesCount;
  }, [pagesCount]);

  useEffect(() => {
    if (resetKeyRef.current === resetKey) return;

    resetKeyRef.current = resetKey;
    setIsAutoLoadStopped(false);
  }, [resetKey]);

  const loadNextPage = useCallback(() => {
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    const pagesBefore = pagesCountRef.current;

    fetchNextPage({ cancelRefetch: false })
      .then((result) => {
        const pagesAfter = result.data?.pages.length ?? 0;
        // Отсутствие прироста — отказ только пока продолжение заявлено: исчерпанный список
        // приходит таким же результатом, и остановка на нём выдала бы отказ за конец списка.
        if (result.isError || (result.hasNextPage && pagesAfter <= pagesBefore)) {
          setIsAutoLoadStopped(true);
        }
      })
      .catch(() => {
        setIsAutoLoadStopped(true);
      })
      .finally(() => {
        isLoadingRef.current = false;
      });
  }, [fetchNextPage]);

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadNextPage,
    hasMore: hasNextPage && !isAutoLoadStopped,
    isLoading: isFetchingNextPage,
    threshold,
    root,
  });

  const resumeAutoLoad = useCallback(() => {
    setIsAutoLoadStopped(false);
  }, []);

  return { sentinelRef, isAutoLoadStopped, resumeAutoLoad };
};
